import {
  WSAvatarProps,
  WSCard,
  WSElement,
  WSIconName
} from "@wingspanhq/fe-component-library";
import { useEffect, useMemo } from "react";
import { useUserId } from "../../../../query/hooks/helpers";
import { Quote } from "../../../../shared/components/Quote";
import { useLocalStorage } from "../../../../shared/hooks/useLocalStorage";
import { useOnVisible } from "../../../../shared/hooks/useOnVisible";
import styles from "./Promotion.module.scss";

export type PromotionVariant = {
  avatar: WSAvatarProps;
  title: string;
  quote: {
    icon: WSIconName;
    title: string;
    content: React.ReactNode;
  };
  action: {
    label: string;
  };
};

type PromotionProps = {
  key: string;
  variants: PromotionVariant[];
  onDismiss: () => void;
  onAction: (variantIndex: number) => void;
  onView?: (variantIndex: number) => void;
};

export const Promotion: React.FC<PromotionProps> = props => {
  const userId = useUserId();
  const [activeVariant, setActiveVariant] = useLocalStorage(
    `${userId}:promotion-${props.key}-activeVariant`,
    0
  );

  const [ref] = useOnVisible(() => {
    props.onView?.(activeVariant);
  });

  const variant = useMemo(() => {
    if (typeof activeVariant !== "number") {
      return;
    }

    return props.variants[activeVariant];
  }, [activeVariant, props.variants]);

  // Rotate variant on every mount
  useEffect(() => {
    setActiveVariant(prev => {
      const nextVariant = prev + 1;
      if (nextVariant >= props.variants.length) {
        return 0;
      }
      return nextVariant;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.variants.length]);

  if (!variant) {
    return null;
  }

  return (
    <WSCard
      className={styles.promotion}
      header={{
        label: {
          avatar: variant.avatar,
          text: variant.title
        },
        value: {
          action: {
            icon: "exit",
            onClick: props.onDismiss,
            onlyIcon: true
          }
        }
      }}
      actions={{
        alignment: "left",
        buttons: [
          {
            kind: "Link",
            label: variant.action.label,
            onClick: () => {
              props.onAction(activeVariant);
            },
            rightIcon: "chevron-right"
          }
        ]
      }}
    >
      <WSElement ref={ref}></WSElement>
      <Quote icon={variant.quote.icon} title={variant.quote.title}>
        {variant.quote.content}
      </Quote>
    </WSCard>
  );
};
